import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { toast } from 'sonner'
import { AuthLayout } from '@/layout/AuthLayout'

export function ForgotPassword() {
  const [step, setStep] = useState<'request' | 'confirm'>('request')
  const [username, setUsername] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { forgotPassword, resetPassword } = useAuth()
  const navigate = useNavigate()

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await forgotPassword(username)
      setStep('confirm')
      toast.success('Password reset code sent to your email!')
    } catch (error) {
      toast.error('Failed to send reset code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)

    try {
      await resetPassword(username, code, newPassword)
      toast.success('Password reset successfully!')
      navigate('/auth/signin')
    } catch (error) {
      toast.error('Failed to reset password. Please check your code.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl py-4">
            {step === 'request' ? 'Forgot Password' : 'Reset Password'}
          </CardTitle>
          <CardDescription className="text-xs">
            {step === 'request'
              ? 'Enter your username to receive a reset code'
              : 'Enter the code sent to your email and your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {step === 'request' ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="username"
                  type="text"
                  placeholder="Username or Email"
                  value={username}
                  className="rounded-xl shadow-xl"
                  onChange={e => setUsername(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-xl shadow-xl mt-4"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleConfirmReset} className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="Enter the code from your email"
                  required
                  className="rounded-xl shadow-xl"
                />
              </div>
              <div className="space-y-2">
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  placeholder="New Password"
                  className="rounded-xl shadow-xl"
                />
              </div>
              <div className="space-y-2">
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm New Password"
                  className="rounded-xl shadow-xl"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-xl shadow-xl mt-4"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-2 pt-4">
          <Link
            to="/auth/signin"
            className="text-xs text-blue-700 hover:underline dark:text-blue-400"
          >
            Back to Sign In
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
